import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { LocalePathPipe } from '../../pipes/locale-path-pipe';
import { Logo } from '../../components/logo/logo';
import { CtaLink } from '../../components/cta-link/cta-link';
import { Icon } from '../../components/icon/icon';
import { LangSwitcher } from '../../components/lang-switcher/lang-switcher';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ContentService } from '../../../core/services/content.service';
import { ProductContextService } from '../../../core/services/product-context.service';

/**
 * The site bar: a floating white strip with the products on the left of
 * centre and three actions on the right — sign in, a walkthrough, and the
 * product's own start. Below desktop width the destinations and the same three
 * actions move into a menu, so nothing on the bar is desktop-only.
 */
@Component({
  selector: 'av-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    LocalePathPipe,
    Logo,
    CtaLink,
    Icon,
    LangSwitcher,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header {
  private readonly content = inject(ContentService);
  private readonly context = inject(ProductContextService);
  private readonly analytics = inject(AnalyticsService);

  protected readonly site = this.content.site;
  protected readonly productGroup = computed(() => this.site().nav[0]);
  protected readonly actions = computed(() => this.content.headerActions(this.context.current()));

  /** The pricing of the product in view, or of the site default outside a product area. */
  protected readonly pricingHref = computed(() => this.context.sectionRoute('pricing'));

  protected readonly scrolled = signal(false);
  protected readonly menuOpen = signal(false);

  constructor() {
    // A route change is the menu's job done.
    const sub = inject(Router)
      .events.pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.menuOpen.set(false));
    inject(DestroyRef).onDestroy(() => sub.unsubscribe());
  }

  protected toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected reportLogin(): void {
    this.analytics.signInClicked(this.actions().login.href);
  }

  @HostListener('window:scroll')
  protected onScroll(): void {
    this.scrolled.set(window.scrollY > 8);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.menuOpen.set(false);
  }
}
